import { useNavigate } from "@tanstack/react-router";
import { getControlName, getPartnerEventName } from "@theprelude/core";
import { PartnerEvents, PartnerUsers } from "@theprelude/sdk";
import { useMemo } from "react";
import { z } from "zod";
import { Filter } from "@/lib/filter";
import { DefaultOverlay } from "../../default-overlay";
import { UsersTable } from "../endpoint-tables/users-table";
import {
  buildViewSchema,
  getView,
  ODataObjectFilter,
  ViewDefinition,
} from "./schema";
import { UsersView } from "./views";

const overlay = "users";
const defaultFilters: never[] = [];

export const usersSchema = buildViewSchema({
  overlay,
  views: UsersView,
  default: UsersView.ALL,
});

export type UsersSchema = NonNullable<z.infer<typeof usersSchema>[1]>;

export function UsersOverlay({
  view = UsersView.ALL,
  filters = defaultFilters,
  control,
  instanceId,
}: UsersSchema) {
  const navigate = useNavigate();

  const onFilter = (filters: Filter[]) =>
    navigate({
      to: window.location.pathname,
      params: (prev) => prev,
      search: (prev) => ({
        ...prev,
        overlay: [overlay, { view, filters, control, instanceId }],
      }),
    });

  const { title, description, query } = useMemo(
    () => getView(views, view, control),
    [control, view],
  );

  return (
    <DefaultOverlay title={title}>
      <div className="h-full overflow-hidden p-4 pb-0">
        {description && <div>{description}</div>}
        <UsersTable
          query={query}
          filters={filters}
          control={control}
          instanceId={instanceId}
          onFilter={onFilter}
        />
      </div>
    </DefaultOverlay>
  );
}

const noRegisteredDevicesQuery: ODataObjectFilter<PartnerUsers> = {
  missing_asset_manager: true,
  missing_edr: true,
  missing_vuln_manager: true,
};

const missingMFAQuery: ODataObjectFilter<PartnerUsers> = {
  instances: {
    any: {
      missing_mfa: true,
    },
  },
};

const allControlFailuresQuery: ODataObjectFilter<PartnerUsers> = {
  or: [noRegisteredDevicesQuery, missingMFAQuery],
};

const views: Record<UsersView, ViewDefinition<PartnerUsers>> = {
  [UsersView.ALL]: {
    title: (control) =>
      control ? `${getControlName(control)} Users` : "All Users",
    query: {},
  },
  [UsersView.NO_REGISTERED_DEVICES]: {
    title: "Users with No Registered Devices",
    query: noRegisteredDevicesQuery,
    partnerEvents: [
      PartnerEvents.USER_MISSING_ASSET_MANAGER,
      PartnerEvents.USER_MISSING_EDR,
      PartnerEvents.USER_MISSING_VULN_MANAGER,
    ],
  },
  [UsersView.MISSING_MFA]: {
    title: `Users with ${getPartnerEventName(PartnerEvents.MISSING_MFA)}`,
    query: missingMFAQuery,
    partnerEvents: [PartnerEvents.MISSING_MFA],
  },
  [UsersView.ALL_CONTROL_FAILURES]: {
    title: `Users with Control Failures`,
    query: allControlFailuresQuery,
    partnerEvents: [
      PartnerEvents.USER_MISSING_ASSET_MANAGER,
      PartnerEvents.USER_MISSING_EDR,
      PartnerEvents.USER_MISSING_VULN_MANAGER,
      PartnerEvents.MISSING_MFA,
    ],
  },
};
